'use client';
import { Fragment, builder, Builder, withChildren } from '@builder.io/react';
import { colorTokens } from './lib/design-tokens/colors';
import {
  fontFamilyTokens,
  fontSizeTokens,
  lineHeightTokens,
  letterSpacingTokens,
  fontWeightTokens,
} from './lib/design-tokens/typography';
import { spacingTokens } from './lib/design-tokens/spacing';
import dynamic from 'next/dynamic';

// Import widgets through Builder instead of direct import
import '@builder.io/widgets';

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!);

const ssr = true;

// Dynamic imports for all components
const BenefitsBgImage = dynamic(
  () => import('./app/features/features/BenefitsBgImage'),
  { ssr: true }
);
const ContactUsPopup = dynamic(
  () => import('./app/layout/popup/ContactUsPopup'),
  { ssr: false }
);
const FeatureSlider = dynamic(
  () => import('./app/features/swipers/FeatureSlider'),
  { ssr: false }
);

import LandingHero from './app/features/hero/LandingHero';
// import DesignerHero from './app/features/hero/MainHero';
import DesignerButton from './app/shared/buttons/DesignerButton';
const SwiperCarousel = dynamic(
  () => import('./app/features/swipers/swiperCarousel'),
  { ssr: true }
);

// Use the lazy loaded testimonials component to reduce bundle size
const Testimonials = dynamic(
  () =>
    import('./app/features/testimonials/TestimonialsLazy').then(
      (mod) => mod.TestimonialsLazy
    ),
  { ssr: false }
);

const AOSAnimationWrapper = dynamic(
  () => import('./app/shared/animation/AOSAnimationWrapper'),
  { ssr: true }
);
const Benefits = dynamic(
  () =>
    import('./app/features/features/BenefitsSectionLazy').then(
      (mod) => mod.BenefitsSectionLazy
    ),
  { ssr: false }
);
const BenefitsSection = dynamic(
  () => import('./app/features/features/BenefitsSection'),
  { ssr: false }
);
const PanZoomImage = dynamic(() => import('./app/shared/common/PanZoomImage'), {
  ssr: false,
});

export function registerComponents() {
  // Register design tokens
  Builder.register('editor.settings', {
    designTokens: {
      colors: colorTokens,
      spacing: spacingTokens,
      fontFamily: fontFamilyTokens,
      fontSize: fontSizeTokens,
      lineHeight: lineHeightTokens,
      letterSpacing: letterSpacingTokens,
      fontWeight: fontWeightTokens,
    },
  });

  Builder.register('editor.settings', {
    settings: {
      hideDefaultTitleAndSettings: true,
      optimizePredictedTargets: true,
    },
  });

  Builder.register('app.settings', {
    pageLoadingComponent:
      "<div style='text-align: center; padding: 16px;'>Loading Page...</div>",
    contentLoadingComponent:
      "<div style='text-align: center; padding: 16px;'>Loading Page...</div>",
  });

  // Register components with their configurations
  Builder.registerComponent(Fragment, { name: 'Fragment' });
  Builder.registerComponent(BenefitsBgImage, {
    name: 'Benefits Background Image',
    inputs: [],
    noWrap: true,
  });
  Builder.registerComponent(ContactUsPopup, { name: 'ContactUsPopup' });
  Builder.registerComponent(FeatureSlider, {
    name: 'Feature Slider',
    inputs: [
      {
        name: 'cards',
        type: 'list',
        subFields: [
          {
            name: 'id',
            type: 'number',
            defaultValue: 1,
          },
          {
            name: 'title',
            type: 'string',
            defaultValue: 'Feature Title',
          },
          {
            name: 'description',
            type: 'string',
            defaultValue: 'Feature description goes here',
          },
          {
            name: 'image',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
            defaultValue: '/placeholder.jpg',
          },
        ],
        defaultValue: [
          {
            id: 1,
            title: 'Sample Feature 1',
            description: 'Description for feature 1',
            image: '/placeholder.jpg',
          },
        ],
      },
    ],
  });

  Builder.registerComponent(LandingHero, { name: 'LandingHero', noWrap: true });
  // Builder.registerComponent(DesignerHero, {
  //   name: 'HomePageHero',
  //   inputs: [],
  //   noWrap: true,
  // });
  Builder.registerComponent(DesignerButton, {
    name: 'DesignerButton',
    inputs: [
      {
        name: 'text',
        type: 'string',
        defaultValue: 'Button',
        required: true,
      },
      {
        name: 'variant',
        type: 'string',
        enum: [
          'primary',
          'secondary',
          'tertiary',
          'outline',
          'success',
          'danger',
        ],
        defaultValue: 'primary',
        required: true,
      },
      {
        name: 'size',
        type: 'string',
        enum: ['small', 'medium', 'large'],
        defaultValue: 'medium',
      },
      {
        name: 'outline',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'disabled',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'type',
        type: 'string',
        enum: ['button', 'submit', 'reset'],
        defaultValue: 'button',
      },
      {
        name: 'href',
        type: 'url',
        helperText:
          'Optional link URL - if provided, button will render as a link',
      },
      {
        name: 'ariaLabel',
        type: 'string',
        helperText: 'Accessibility label for screen readers',
      },
      {
        name: 'className',
        type: 'string',
        helperText: 'Additional CSS classes',
      },
    ],
    defaultStyles: {
      display: 'inline-block',
    },
  });
  Builder.registerComponent(withChildren(SwiperCarousel), {
    name: 'SwiperCarousel',
    noWrap: true,
    inputs: [
      {
        name: 'slides',
        type: 'list',
        subFields: [
          {
            name: 'image', // Changed from imageSrc
            type: 'file',
            allowedFileTypes: ['jpeg', 'png', 'svg'],
            defaultValue: '',
            required: true,
          },
          {
            name: 'title', // Changed from heading
            type: 'text',
            defaultValue: 'Heading Text',
            required: false,
          },
          {
            name: 'subtitle', // Changed from subtext
            type: 'text',
            defaultValue: 'Subtext for the slide',
            required: false,
          },
          {
            name: 'alt', // Added missing alt field
            type: 'text',
            defaultValue: 'Slide image',
            required: true,
          },
          {
            name: 'buttonText',
            type: 'text',
            defaultValue: 'Discover More',
            required: false,
          },
          {
            name: 'buttonLink',
            type: 'text',
            defaultValue: '/features-benefits',
            required: false,
          },
          {
            name: 'bgOverlay',
            type: 'boolean',
            defaultValue: false,
            required: true,
            helperText: 'Enable background overlay on slide',
          },
          {
            name: 'imgPosition',
            type: 'enum',
            enum: [
              'top left', // '0% 0%'
              'top center', // '50% 0%'
              'top right', // '100% 0%'
              'center left', // '0% 50%'
              'center', // '50% 50%'
              'center right', // '100% 50%'
              'bottom left', // '0% 100%'
              'bottom center', // '50% 100%'
              'bottom right', // '100% 100%'
            ],
            defaultValue: 'center',
            required: false,
            helperText: 'Select the focal point of the image',
          },
        ],
      },
    ],
    canHaveChildren: true,
  });

  Builder.registerComponent(Testimonials, {
    name: 'Testimonials',
    noWrap: true,
    defaultStyles: {
      lazyLoad: 'true', // Ensure lazy loading is enabled
    },
  });

  Builder.registerComponent(withChildren(AOSAnimationWrapper), {
    name: 'AOSAnimationWrapper',
    canHaveChildren: true,
    inputs: [
      {
        name: 'animation',
        type: 'string',
        enum: [
          'fade-up',
          'fade-down',
          'fade-left',
          'fade-right',
          'fade-up-right',
          'fade-up-left',
          'fade-down-right',
          'fade-down-left',
          'zoom-in',
          'zoom-in-up',
          'zoom-in-down',
          'zoom-in-left',
          'zoom-in-right',
          'zoom-out',
          'zoom-out-up',
          'zoom-out-down',
          'zoom-out-left',
          'zoom-out-right',
          'slide-up',
          'slide-down',
          'slide-left',
          'slide-right',
          'flip-left',
          'flip-right',
          'flip-up',
          'flip-down',
        ],
        defaultValue: 'fade-up',
      },
      {
        name: 'duration',
        type: 'number',
        defaultValue: 1000,
      },
      {
        name: 'delay',
        type: 'number',
        defaultValue: 0,
      },
      {
        name: 'offset',
        type: 'number',
        defaultValue: 100,
      },
      {
        name: 'once',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'mirror',
        type: 'boolean',
        defaultValue: false,
      },
    ],
  });
  Builder.registerComponent(Benefits, { name: 'Benefits Section' });
  Builder.registerComponent(BenefitsSection, { name: 'BenefitsSection' });
  Builder.registerComponent(PanZoomImage, { name: 'PanZoomImage' });
  Builder.registerComponent(PanZoomImage, { name: 'PanZoomImage2' });
}

// Ensure registration happens on import
registerComponents();
